<template>
  <NuxtLayout :name="layoutName">
    <Maintenance
      v-if="isMaintenance || parametersNotProvided"
      :status="isMaintenance ? 'maintenance' : 'noparams'"
    />
    <RedirectionPage v-else-if="isWebsiteRedirectionActive" />
    <template
      v-for="(section, index) of page?.sections"
      v-else
      :key="section.id + `_${index}`"
    >
      <Section
        v-if="!canVisibleSurveyHeader(section)"
        :section="section"
        :locale="language"
        :page-data="pageData"
        :page-options="pageOptions"
        :style="{
          ...section.style,
          ...(
            ((hasFooter && section.type === 'footer') || (!hasFooter && section.type === 'zotloFooter'))
              ? { marginTop: 'auto'}
              : {}
          )
        }"
      >
        <template #default="{ elements }">
          <ElementWrapper
            v-for="el of elements"
            :key="el.id"
            :page-data="pageData"
            :el="el"
            :locale="language"
            :page-options="pageOptions"
            :is-preview-mode="isPreview"
          />
        </template>
      </Section>
    </template>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { useHead } from 'unhead';
import { useSiteData } from '@/composables/useSiteData';
import type { PageType } from '@/types';
import { usePage } from '@/composables/usePage';
import { getBaseUrl } from '@/utils/getBaseUrl';
import { getFirstPage } from '@/utils/session';
import { setNamedCookie, COOKIE } from '@/utils/cookie';
import getParamsFromQueryString from '@/utils/getParamsFromQueryString';
import { globalListener, globalRemoveListener } from '@shared/utils/helpers';
import '@shared/utils/emitter';
import type { Section as SectionType } from '@shared/types/model';
import type { QuestionTypeEnum} from '@shared/types/quiz';
import { resultPages } from '@shared/types/quiz';
import { useSurveyStore } from '@/stores/survey';
import RedirectionPage from '@/components/redirection-page/index.vue';
import Maintenance from '@/components/maintenance.vue';

(globalThis as any).__CDN_URL = useRuntimeConfig().NUXT_APP_CDN_URL || '';

const route = useRoute();
const { language, isStoreFlow, isPreview, isSandbox, siteData } = useSiteData();
const { currentQuestion } = storeToRefs(useSurveyStore());
const { pageType, slug } = getBaseUrl(route?.fullPath);
const { page, pageData, pageOptions, head, isWebsiteRedirectionActive, isMaintenance, countdownStartedAtCookie, parametersNotProvided } = usePage(pageType as PageType || getFirstPage());
const hasFooter = computed(() => {
  return page.value?.sections?.find((section) => section.type === 'footer');
})

const layoutName = computed(() => {
  if (isStoreFlow?.value) return "store-flow";
  if (isSandbox?.value) return "sandbox";
  return "default";
});

if (import.meta.client) {
  const queryObject = getParamsFromQueryString(window.location.href);
  // Save UTM info as cookie
  const utmInfo = Object.entries(queryObject).reduce((acc, [key, value]) => {
    if (key && /^utm_/g.test(key?.trim())) acc[key?.trim()] = value;
    return acc;
  }, {} as Record<string, any>);

  if (Object.keys(utmInfo).length > 0) {
    setNamedCookie({
      cookieName: COOKIE.UTM,
      value: JSON.stringify(utmInfo),
      path: slug
    })
  }

  globalListener('countdownStarted', onCountdownStarted);
}

function onCountdownStarted(startedAt: number) {
  if (countdownStartedAtCookie) return;
  const expireAt = siteData.value?.expireAt || getNamedCookie(COOKIE.EXPIRE_AT);
  setNamedCookie({
    cookieName: COOKIE.COUNTDOWN_STARTED_AT,
    value: JSON.stringify(startedAt),
    exdays: expireAt as any,
    path: slug
  })
}

function canVisibleSurveyHeader(section: SectionType) {
  return (
    (
      resultPages.includes(currentQuestion.value?.type as QuestionTypeEnum) ||
      !currentQuestion.value
    ) &&
    section.type === 'surveyheader'
  )
}

useHead(head);

onBeforeUnmount(() => {
  globalRemoveListener('countdownStarted', onCountdownStarted);
})
</script>

<style lang="postcss">
a {
  @apply underline;

  &:-webkit-any-link {
    color: -webkit-link;
  }
}
</style>
